import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Subscription, BehaviorSubject,  } from 'rxjs';
import { tap, switchMap, filter } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';

import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { FilterTypeModel } from '../../../../Common/Filter/models/abstract-filter-type.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SocialRouteModel } from '../../../SocialRouter/models/social-route.model';
import { SocialRouteTypeEnum } from '../../../SocialRouter/enums/social-route-type.enum';
import { SocialRoutingService } from '../../../Core/services/social-routing.service';
import { ThemeService } from '../../services/theme.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { SocialSearchService } from '../../../Core/services/social-search.service';
import { SocialSeriesService } from '../../services/social-series.service';
import { Theme } from '../../models/theme.model';
import { AgentWidgetComponent, AgentWidgetMode } from '../AgentWidget/agent-widget.component';
import { ArchiveSearchWidgetComponent, ArchiveSearchMode } from '../ArchiveSearchWidget/archive-search-widget.component';
import { AgentFilterComponent } from '../AgentFilter/agent-filter.component';
import { SocialArchiveSearchService } from '../../services/social-archive-search.service';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { AgentSourceIndexService } from '../../../../AutomationAgents/services/agent-source-index.service';
import { ManageSourceAgentsService } from '../../../../AutomationAgents/services/manage-source-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';


@Component({
    selector: 'agpr-theme-sidebar',
    templateUrl: './theme-sidebar.component.html',
    styleUrls: ['./theme-sidebar.component.scss'],
    animations: [
        trigger('containerState', [
            state('hidden', style({
                opacity: 0,
                height: 0
            })),
            state('visible', style({
                opacity: 1,
                height: '*'
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class ThemeSidebarComponent implements OnInit, AfterViewInit, OnDestroy {

    readonly id: string = 'theme';
    readonly socialRouteTypeEnum = SocialRouteTypeEnum;
    readonly sidebarItemOrder: SocialRouteTypeEnum[] = [
        SocialRouteTypeEnum.analytics,
        SocialRouteTypeEnum.postFeed,
    ];
    readonly sidebarItemHeight: number = 50;
    readonly sidebarPadding: number = 15;
    readonly buttonTypes = ButtonTypesEnum;
    readonly archiveSearchMode = ArchiveSearchMode;
    readonly severityEnum = messageSeverityEnum;
    readonly componentStyle = AgprComponentStyleEnum;
    readonly routeTypeToRouteLinkMap = new Map<SocialRouteTypeEnum, string>([
        [ SocialRouteTypeEnum.analytics, '/analytics'        ],
        [ SocialRouteTypeEnum.postFeed, '/postFeed'          ],
        [ SocialRouteTypeEnum.archiveAnalytics, '/analytics' ],
        [ SocialRouteTypeEnum.archivePostFeed, '/postFeed'   ]
    ]);
    readonly saveActions: MenuItem[] = [
        {
            label: this.localizationService.transform('Social.intelligence.theme.lhp.actions.editSearch'),
            command: () => this.openArchiveSearchWidget(ArchiveSearchMode.edit)
        },
        {
            label: this.localizationService.transform('Social.intelligence.theme.lhp.actions.saveAsTopic'),
            command: () => this.openArchiveSearchWidget(ArchiveSearchMode.saveAs)
        }
    ];

    changesPending = false;
    onScreenAgentIds: number[];

    @ViewChild('sidebarLine') sideBarLine: ElementRef;
    @ViewChild('agentFilter') agentFilter: AgentFilterComponent;

    socialRouteModel: SocialRouteModel = null;
    currentTheme: Theme = null;
    themeAgents: MediaSubscription[] = [];
    themes: Theme[] = null;

    /** Automation agents live on each theme, keyed by AgentSourceIndexService.key */
    liveAgentCounts: Map<string, number> = new Map<string, number>();
    hasAgenticFeature: boolean = false;
    showThemeDropdown: boolean = false;
    maxReachedTooltip: string;

    overlayContainer: Element;
    analyticsRouterLink: string;
    postFeedRouterLink: string;
    enableEngagementInsights: boolean;
    isArchiveSearch: boolean = false;


    activeFilters: FilterTypeModel[] = [];
    searchDateRange: DateQueryModel;
    agentIds: number[] = [];

    activeArchiveSearch: MediaSubscription;

    search: MentionSearchModel;
    overrideAgentIds: boolean = false;

    private subscriptions: Subscription[] = [];
    private socialSearchCriteria: MentionSearchCriteriaModel;
    private pendingAgents: number[];

    constructor(
        public localizationService: LocalizationService,
        private socialRoutingService: SocialRoutingService,
        private themeService: ThemeService,
        private router: Router,
        public route: ActivatedRoute,
        private ref: ElementRef,
        private coreDialogService: CoreDialogService,
        private socialSearchService: SocialSearchService,
        private socialSeriesService: SocialSeriesService,
        public titleService: Title,
        private socialArchiveSearchService: SocialArchiveSearchService,
        private agentSourceIndexService: AgentSourceIndexService,
        private currentUserStateService: CurrentUserStateService,
        private manageSourceAgentsService: ManageSourceAgentsService
    ) {
        this.subscriptions.push(
            this.themeService.all$.subscribe(themes => this.themes = themes),
            // Themes an automation agent is watching, marked here as they are on the theme cards
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => this.liveAgentCounts = counts),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => this.hasAgenticFeature = enabled),
            this.socialSearchService.getSearchCriteria().subscribe(searchCriteria => this.socialSearchCriteria = searchCriteria),
            this.socialRoutingService.isArchiveSearch$.subscribe(isArchiveSearch => this.isArchiveSearch = isArchiveSearch),
            this.socialArchiveSearchService.activeArchiveSearch$.subscribe(activeArchiveSearch => this.activeArchiveSearch = activeArchiveSearch)
        );

    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            // Put this service here to avoid the race condition that isArchive search is subscribed after currentTheme
            this.themeService.currentResource$.pipe(
                filter(x => !!x),
                tap(currentTheme => {
                    if (this.isArchiveSearch) {
                        return;
                    }

                    this.currentTheme = currentTheme;
                    const pageSize = this.socialSearchService.pageSize;
                    this.socialSearchService.clearSearch();
                    // keep the page size when changing themes
                    this.socialSearchService.setPageSize(pageSize);
                    this.socialSearchService.clearFilters();
                    this.socialSeriesService.clearFilters();

                    this.handleFiltersFromRoute();

                    // Deal with initial load page is post feed page scenario
                    this.agentIds = this.currentTheme.subscriptionIds;
                    if (!this.socialSearchCriteria) {
                        const searchCriteria = this.socialSearchService.createMentionSearchCriteriaModel();
                        this.socialSearchService.setSearchCriteria(searchCriteria);
                    }

                    if (!this.overrideAgentIds) {
                        this.socialSearchService.setAgents(this.agentIds);
                        this.socialSeriesService.setAgents(this.agentIds);
                        this.onScreenAgentIds = this.agentIds;
                    }
                }),
                switchMap((theme) => this.themeService.getAgentsForTheme(theme.id))
            ).subscribe(
                themeAgents => {
                    this.themeAgents = themeAgents;

                    // Update currentTheme.subscriptions to match updated themeAgents
                    const newSubscriptions = themeAgents.map(media => media.id);
                    if (this.currentTheme.subscriptionIds.length !== newSubscriptions.length) {
                        this.currentTheme.subscriptionIds = newSubscriptions;
                    }

                }
            ),
        );
    }

    ngAfterViewInit() {
        this.subscriptions.push(
            this.socialRoutingService.getCurrentRoute$().subscribe(
                route => {
                    // To prevent expression changed error
                    requestAnimationFrame(() => {
                        if (route.type === SocialRouteTypeEnum.archiveAnalytics || route.type === SocialRouteTypeEnum.archivePostFeed) {
                            this.analyticsRouterLink =  '/Social/archive/results/analytics';
                            this.postFeedRouterLink = '/Social/archive/results/postFeed';
                        } else {
                            this.analyticsRouterLink =  `/Social/themes/theme/${route?.value}/analytics`;
                            this.postFeedRouterLink = `/Social/themes/theme/${route?.value}/postFeed`;
                        }

                        if (this.socialRouteModel === null) {
                            let elementName;
                            switch (route.type) {
                                case SocialRouteTypeEnum.analytics:
                                    elementName = 'analytics';
                                    break;
                                case SocialRouteTypeEnum.postFeed:
                                    elementName = 'postFeed';
                                    break;
                                case SocialRouteTypeEnum.archiveAnalytics:
                                case SocialRouteTypeEnum.archivePostFeed:
                                    elementName = route.type === SocialRouteTypeEnum.archiveAnalytics ? 'analytics' : 'postFeed';
                                    break;
                            }
                            const activeElement = document.querySelector('#' + elementName);
                            this.animateSidebar(activeElement as HTMLElement);
                        }
                        this.socialRouteModel = route;
                    });
                }
            )
        );
    }

    handleFiltersFromRoute() {
        // handle loading filters from query params first
        if (this.route.snapshot.data.filters) {
            this.search = new MentionSearchModel();
            this.search.searchCriteria.query = this.route.snapshot.data.filters.query;
            if (this.search.searchCriteria.query) {
                this.socialSearchService.setDateQuery(this.search.searchCriteria.query.dateQuery);
                this.socialSearchService.populateFiltersFromSearch(this.search.searchCriteria.query);

                this.search.searchCriteria.query.theme.include = [];

                if (this.search.searchCriteria.query.agent?.include?.length) {
                    this.overrideAgentIds = true;
                    this.socialSearchService.setAgents(this.search.searchCriteria?.query.agent.include.slice());
                    this.socialSeriesService.setAgents(this.search.searchCriteria?.query.agent.include.slice());
                }

                this.socialSearchService.setSortField(this.search.searchCriteria.sortField);
                this.socialSearchService.setSearchCriteria(this.search.searchCriteria);
            }
        } else {
            this.overrideAgentIds = false;
        }
    }

    animateSidebar(item: HTMLElement) {
        const lineTop = (this.sideBarLine.nativeElement as HTMLElement).offsetTop;
        const itemTop = item?.offsetTop;
        const translateValue = itemTop - lineTop - 5;
        this.sideBarLine.nativeElement.style.transform = 'translateY(' + translateValue + 'px)';
    }

    /** How many automation agents are live on a theme, for the marker beside its name */
    liveAgentCount(theme: Theme): number {
        return theme?.id ? this.liveAgentCounts.get(
            this.agentSourceIndexService.key(MonitoringSeriesTypeEnum.theme, theme.id)
        ) ?? 0 : 0;
    }

    liveAgentTooltip(theme: Theme): string {
        return this.localizationService.transform('AutomationAgents.liveAgent', { count: `${this.liveAgentCount(theme)}` });
    }

    /**
     * Opens the panel that manages the automation agents watching the theme in view. The filters
     * applied to the theme go with it, so an agent added from it watches what is on screen.
     */
    addLiveAgent(): void {
        // Each tab filters through its own service - the post feed's, or the analytics charts'
        const filterService = this.socialRouteModel?.type === SocialRouteTypeEnum.analytics
            ? this.socialSeriesService
            : this.socialSearchService;

        this.manageSourceAgentsService.open(
            MonitoringSeriesTypeEnum.theme,
            this.currentTheme?.id,
            // The social filters, not the inherited monitoring ones, which sit empty on a theme
            filterService.generateSearchFromFilters(false)
        );
    }

    openCreateAgentDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                theme: this.currentTheme,
                mode: AgentWidgetMode.create
            },
            outputs: {}
        };
        this.coreDialogService.open(AgentWidgetComponent, io);
    }

    toggleThemeDropdown(event?: KeyboardEvent): void {
        if (!event || event.key === 'Enter' || event.key === ' ') {
            this.showThemeDropdown = !this.showThemeDropdown;
        }
    }

    handleThemeDropdownItemKeyboardInput(event: KeyboardEvent, theme: Theme) {
        if (event.key === 'Enter' || event.key === ' ') {
            this.router.navigate(['Social/themes/theme', theme.id, 'analytics']);
            this.closeThemeDropdown();
        }
        if (event.key === ActionKeys.ESCAPE) {
            this.closeThemeDropdown();
        }
    }

    closeThemeDropdown(): void {
        if (this.showThemeDropdown) {
            this.showThemeDropdown = false;
        }
    }

    handleManageThemeButtonKeyboardInput(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            // TODO: handle keyboard navigation with routing service
            this.router.navigate(['Social/themes']);
        }
    }

    updateAgentsStatus(enabledAgents: number[]) {
        if (enabledAgents.sort().join(',') !== this.onScreenAgentIds.sort().join(',')) {
            this.changesPending = true;
            this.pendingAgents = enabledAgents;
        } else {
            this.changesPending = false;
        }
    }

    confirmChange(){
        this.socialSearchService.setAgents(this.pendingAgents);
        if (this.socialRouteModel.type === SocialRouteTypeEnum.postFeed) {
            this.socialSearchService.search(true, false);
        } else if (this.socialRouteModel.type === SocialRouteTypeEnum.analytics) {
            this.socialSeriesService.setAgents(this.pendingAgents);
            this.socialSeriesService.doSearch(true);
        }
        this.onScreenAgentIds = this.pendingAgents;
        this.changesPending = false;
    }

    cancelChanges() {
        this.agentFilter.externalUpdate(this.onScreenAgentIds);
    }

    openArchiveSearchWidget(archiveSearchMode: ArchiveSearchMode, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.showThemeDropdown = false;
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                archiveMode: archiveSearchMode,
                searchModel: archiveSearchMode === ArchiveSearchMode.create ? null : this.activeArchiveSearch
            },
            outputs: {}
        };
        this.coreDialogService.open(ArchiveSearchWidgetComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.socialSearchService.setPageSize(25);
        this.socialSearchService.clearSearch();
    }
}
